import { Overlay, OverlayRef } from '@angular/cdk/overlay';
import { TemplatePortal } from '@angular/cdk/portal';
import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  DestroyRef,
  EventEmitter,
  inject,
  Input,
  OnInit,
  Output,
  TemplateRef,
  ViewChild,
  ViewContainerRef,
} from '@angular/core';
import { FormBuilder, ReactiveFormsModule } from '@angular/forms';
import { MatButton } from '@angular/material/button';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatIconModule } from '@angular/material/icon';
import { MatLegacyFormFieldModule } from '@angular/material/legacy-form-field';
import { MatLegacyInputModule } from '@angular/material/legacy-input';
import { ConfirmDialogComponent } from '@shared/components/widgets/dialog/confirm/confirm.component';
import { Task, TaskRelatedToModule, TaskView } from '@tasks/tasks.types';
import { debounceTime, filter, noop, tap } from 'rxjs';
import { MatLegacyDialog as MatDialog } from '@angular/material/legacy-dialog';
import moment from 'moment';
import { MatLegacyButtonModule } from '@angular/material/legacy-button';
import { TsInputComponent } from 'tsui';
import { DirectivesModule } from '@shared/directives/directives.module';
import { MatLegacySelectModule } from '@angular/material/legacy-select';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Store } from '@ngxs/store';
import { TaskActions } from '@tasks/state/actions';
import { ConfirmationDialogService } from '@shared/components/widgets/dialog/confirmation/confirmation-dialog.service';
import { TaskUtils } from '@tasks/tasks.utils';
import { TaskPredecessorService } from '@tasks/services/task-predecessor.service';
import { AccountState } from '@state/account/state';
import { FeatureFlag } from '@core/enums/feature-flag';
import { TaskState } from '@tasks/state/state';
import { RelatedToType } from '@shared/enums';

@Component({
  selector: 'app-task-details-start-due-date',
  standalone: true,
  imports: [
    CommonModule,
    MatIconModule,
    MatDatepickerModule,
    ReactiveFormsModule,
    TsInputComponent,
    MatLegacyFormFieldModule,
    MatLegacyInputModule,
    MatLegacyButtonModule,
    MatLegacySelectModule,
    DirectivesModule,
  ],
  template: `
    <div class="flex gap-2 justify-start flex-wrap items-center" [formGroup]="dateForm">
      <!-- Start date -->
      @if (this.isTask) {
        <div>
          <div class="text-cool-gray-400">Start date</div>
          <button
            [appAuthorize]="canEditTask"
            denyAction="disable"
            class="m-0 p-0 date-button"
            mat-button
            (click)="editStartDate()"
            #startDatePanelOrigin
          >
            <mat-icon [svgIcon]="'today'" class="mr-2"></mat-icon>
            @if (task.start_date) {
              <span>{{ task.start_date | date: 'mediumDate' }}</span>
            } @else {
              <span>Not set</span>
            }
          </button>

          <!-- Start date panel -->
          <ng-template #startDatePanel>
            <div class="tasks-details-due-date-panel bg-white shadow-lg pb-2">
              <mat-calendar
                [selected]="dateForm.get('start_date').value"
                [startAt]="dateForm.get('start_date').value"
                (selectedChange)="updateStartDate($event)"
              ></mat-calendar>

              <button
                class="ml-1"
                mat-stroked-button
                *ngIf="dateForm.get('start_date').value"
                (click)="removeStartDate()"
              >
                <mat-icon [svgIcon]="'remove_circle'"></mat-icon>
                Remove start date
              </button>
            </div>
          </ng-template>
        </div>
      }

      <!-- Due date -->
      <div>
        <div class="text-cool-gray-400">Due date</div>
        <button
          mat-button
          class="date-button"
          [appAuthorize]="canEditTask"
          denyAction="disable"
          data-testid="task-details-due-date-button"
          [ngClass]="{ overdue: isOverdue(), 'due-set': task?.due_date }"
          (click)="editDueDate()"
          [disabled]="isTargetKeyDate"
          #dueDatePanelOrigin
        >
          <mat-icon [svgIcon]="'today'" class="mr-2"></mat-icon>
          @if (task.due_date) {
            <span>{{ task.due_date | date: 'mediumDate' }}</span>
          } @else {
            <span>Not set</span>
          }
        </button>

        <!-- Due date panel -->
        <ng-template #dueDatePanel>
          <div class="tasks-details-due-date-panel bg-white shadow-lg pb-2">
            <mat-calendar
              [minDate]="this.isKeyDate ? undefined : dateForm.get('start_date').value"
              [selected]="dateForm.get('due_date').value"
              [startAt]="this.isKeyDate ? undefined : dateForm.get('start_date').value"
              (selectedChange)="updateDueDate($event)"
              data-testid="task-details-due-date-calendar"
            ></mat-calendar>

            <button
              class="ml-1"
              mat-stroked-button
              *ngIf="dateForm.get('due_date').value"
              (click)="removeDueDate()"
              data-testid="task-details-remove-due-date"
            >
              <mat-icon [svgIcon]="'remove_circle'"></mat-icon>
              Remove due date
            </button>
          </div>
        </ng-template>
      </div>

      @if (this.isTask) {
        <div class="w-full flex mt-2">
          <div class="mr-2">
            <div class="due-date duration-field">
              <div class="label">Duration</div>
              <mat-form-field class="w-full treo-mat-no-subscript">
                <input matInput [formControlName]="'duration'" [appAuthorize]="canEditTask" denyAction="disable" />
              </mat-form-field>
            </div>
          </div>
          <div class="due-date flex-auto mr-2">
            <div class="label">Duration Type</div>
            <mat-form-field class="w-full treo-mat-no-subscript">
              <mat-select [formControlName]="'duration_type'" [appAuthorize]="canEditTask" denyAction="disable">
                <mat-option [value]="'business_days'">Business Days</mat-option>
                <mat-option [value]="'calendar_days'">Calendar Days</mat-option>
              </mat-select>
            </mat-form-field>
          </div>
        </div>
      }
    </div>
  `,
  styleUrl: './task-details-start-due-date.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class TaskDetailsStartDueDateComponent implements OnInit {
  private fb = inject(FormBuilder);
  private _overlay = inject(Overlay);
  private _task: Task;
  private _changeDetectorRef = inject(ChangeDetectorRef);
  private _viewContainerRef = inject(ViewContainerRef);
  private store = inject(Store);
  private taskPredecessorService = inject(TaskPredecessorService);

  private _dueDatePanelOverlayRef: OverlayRef;
  private _dueDatePanelTemplatePortal: TemplatePortal;
  private _startDatePanelOverlayRef: OverlayRef;
  private _startDatePanelTemplatePortal: TemplatePortal;
  private confirmationService = inject(ConfirmationDialogService);
  private matDialog = inject(MatDialog);
  destroyRef = inject(DestroyRef);

  @ViewChild('dueDatePanelOrigin')
  private _dueDatePanelOrigin: MatButton;

  @ViewChild('startDatePanelOrigin')
  private _startDatePanelOrigin: MatButton;

  @ViewChild('dueDatePanel')
  private _dueDatePanel: TemplateRef<any>;

  @ViewChild('startDatePanel')
  private _startDatePanel: TemplateRef<any>;

  @Input() canEditTask: boolean;

  @Input()
  set task(value: Task) {
    this._task = value;
    if (this.dateForm) {
      this.dateForm.patchValue(
        {
          start_date: value.start_date,
          due_date: value.due_date,
          duration: value.duration,
          duration_type: value.duration_type,
          completed_at: value.completed_at,
        },
        { emitEvent: false },
      );
    }
  }

  @Output() updateTask = new EventEmitter<Partial<Task>>();

  get task(): Task {
    return this._task;
  }

  get isKeyDate(): boolean {
    return this.task.task_type === 'key_date';
  }

  get isTargetKeyDate(): boolean {
    return this.task.task_type === 'key_date' && !!this.task.linked_key_date_field_as_target;
  }

  dateForm;

  ngOnInit() {
    this.dateForm = this.fb.group({
      workflow_state: [this.task.workflow_state],
      start_date: [this.task.start_date],
      due_date: [this.task.due_date],
      duration: [this.task.duration],
      duration_type: [this.task.duration_type],
      completed_at: [this.task.completed_at],
    });

    // TODO MJR
    // MOVE IT TO SHELL COMPONENT WHE THE  UPDATE ACTIONS ONLY SEND THE CHANGED FIELDS
    this.dateForm
      .get('start_date')
      .valueChanges.pipe(debounceTime(500), takeUntilDestroyed(this.destroyRef))
      .subscribe((value) => {
        const taskUpload = {
          id: this.task.id,
          task_list_id: this.task.task_list_id,
          start_date: value,
          predecessors: this.task.predecessors,
        };

        this.store.dispatch(new TaskActions.UpdateTask(taskUpload));
      });

    this.dateForm
      .get('due_date')
      .valueChanges.pipe(debounceTime(500), takeUntilDestroyed(this.destroyRef))
      .subscribe((value) => {
        const taskUpload = {
          id: this.task.id,
          task_list_id: this.task.task_list_id,
          due_date: value,
          predecessors: this.task.predecessors,
        };

        this.store.dispatch(new TaskActions.UpdateTask(taskUpload));
      });

    this.dateForm
      .get('duration')
      .valueChanges.pipe(debounceTime(500), takeUntilDestroyed(this.destroyRef))
      .subscribe((value) => {
        const taskUpload = {
          id: this.task.id,
          task_list_id: this.task.task_list_id,
          duration: value,
          predecessors: this.task.predecessors,
        };

        this.store.dispatch(new TaskActions.UpdateTask(taskUpload));
      });

    this.dateForm
      .get('duration_type')
      .valueChanges.pipe(debounceTime(500), takeUntilDestroyed(this.destroyRef))
      .subscribe((value) => {
        const taskUpload = {
          id: this.task.id,
          task_list_id: this.task.task_list_id,
          duration_type: value,
        };

        this.store.dispatch(new TaskActions.UpdateTask(taskUpload));
      });
  }

  /**
   * Open due date panel
   */
  openDueDatePanel(): void {
    // Create the overlay
    this._dueDatePanelOverlayRef = this._overlay.create({
      backdropClass: '',
      hasBackdrop: true,
      scrollStrategy: this._overlay.scrollStrategies.block(),
      positionStrategy: this._overlay
        .position()
        .flexibleConnectedTo(this._dueDatePanelOrigin?._elementRef.nativeElement)
        .withFlexibleDimensions()
        .withViewportMargin(64)
        .withLockedPosition()
        .withPositions([
          {
            originX: 'end',
            originY: 'bottom',
            overlayX: 'end',
            overlayY: 'top',
          },
          {
            originX: 'start',
            originY: 'bottom',
            overlayX: 'start',
            overlayY: 'top',
          },
        ]),
    });

    // Create a portal from the template
    this._dueDatePanelTemplatePortal = new TemplatePortal(this._dueDatePanel, this._viewContainerRef);

    // Attach the portal to the overlay
    this._dueDatePanelOverlayRef.attach(this._dueDatePanelTemplatePortal);

    // Subscribe to the backdrop click
    this._dueDatePanelOverlayRef.backdropClick().subscribe(() => {
      // Close the panel
      this.closeDueDatePanel();
    });
  }

  /**
   * Close the due date panel
   */
  closeDueDatePanel(): void {
    // If overlay exists and attached...
    if (this._dueDatePanelOverlayRef && this._dueDatePanelOverlayRef.hasAttached()) {
      // Detach it
      this._dueDatePanelOverlayRef.detach();
    }

    // If template portal exists and attached...
    if (this._dueDatePanelTemplatePortal && this._dueDatePanelTemplatePortal.isAttached) {
      // Detach it
      this._dueDatePanelTemplatePortal.detach();
    }

    // Mark for check
    this._changeDetectorRef.markForCheck();
  }

  /**
   * Update the due date
   *
   * @param event
   */
  updateDueDate(event: moment.Moment): void {
    // Get the form control for 'dueDate'
    const dueDateFormControl = this.dateForm.get('due_date');

    // Update it
    dueDateFormControl.setValue(event.toISOString());

    // Close the due date panel...
    this.closeDueDatePanel();
  }

  /**
   * Remove the due date
   */
  removeDueDate(): void {
    // Get the form control for 'dueDate'
    const dueDateFormControl = this.dateForm.get('due_date');

    // Clear the due date
    dueDateFormControl.setValue(null);

    // Close the due date panel...
    this.closeDueDatePanel();
  }

  editStartDate() {
    this.editDates('start_date');
  }

  editDueDate() {
    this.editDates('due_date');
  }

  editDates(field: 'start_date' | 'due_date') {
    this.taskPredecessorService.isPredecessorAlertModalNeeded(this.task, field).subscribe((response) => {
      if (response.modalSkipped) {
        return field === 'start_date' ? this.openStartDatePanel() : this.openDueDatePanel();
      }

      if (response.removePredecessors) {
        this.task.predecessors = [];
        this.updateTask.emit(this.task);
        const taskToUnlink = this.task.linked_to[0];
        if (taskToUnlink) {
          this.store.dispatch(new TaskActions.UnlinkTask(taskToUnlink));
        }
        return field === 'start_date' ? this.openStartDatePanel() : this.openDueDatePanel();
      }
    });
  }

  /**
   * Open due date panel
   */
  openStartDatePanel(): void {
    // Create the overlay
    this._startDatePanelOverlayRef = this._overlay.create({
      backdropClass: '',
      hasBackdrop: true,
      scrollStrategy: this._overlay.scrollStrategies.block(),
      positionStrategy: this._overlay
        .position()
        .flexibleConnectedTo(this._startDatePanelOrigin?._elementRef.nativeElement)
        .withFlexibleDimensions()
        .withViewportMargin(64)
        .withLockedPosition()
        .withPositions([
          {
            originX: 'end',
            originY: 'bottom',
            overlayX: 'end',
            overlayY: 'top',
          },
          {
            originX: 'start',
            originY: 'bottom',
            overlayX: 'start',
            overlayY: 'top',
          },
        ]),
    });

    // Create a portal from the template
    this._startDatePanelTemplatePortal = new TemplatePortal(this._startDatePanel, this._viewContainerRef);

    // Attach the portal to the overlay
    this._startDatePanelOverlayRef.attach(this._startDatePanelTemplatePortal);

    // Subscribe to the backdrop click
    this._startDatePanelOverlayRef.backdropClick().subscribe(() => {
      // Close the panel
      this.closeStartDatePanel();
    });
  }

  /**
   * Close the due date panel
   */
  closeStartDatePanel(): void {
    // If overlay exists and attached...
    if (this._startDatePanelOverlayRef && this._startDatePanelOverlayRef.hasAttached()) {
      // Detach it
      this._startDatePanelOverlayRef.detach();
    }

    // If template portal exists and attached...
    if (this._startDatePanelTemplatePortal && this._startDatePanelTemplatePortal.isAttached) {
      // Detach it
      this._startDatePanelTemplatePortal.detach();
    }

    // Mark for check
    this._changeDetectorRef.markForCheck();
  }

  /**
   * Update the due date
   *
   * @param event
   */
  updateStartDate(event: moment.Moment): void {
    // Get the form control for 'dueDate'
    const dueDateFormControl = this.dateForm.get('start_date');

    // Update it
    dueDateFormControl.setValue(event.toISOString());

    // Close the due date panel...
    this.closeDueDatePanel();
  }

  /**
   * Remove the due date
   */
  removeStartDate(): void {
    // Get the form control for 'dueDate'
    const dueDateFormControl = this.dateForm.get('start_date');

    // Clear the due date
    dueDateFormControl.setValue(null);

    // Close the due date panel...
    this.closeDueDatePanel();
  }

  /**
   * Check if the task is overdue or not
   */
  isOverdue(): boolean {
    return moment(this.task.due_date, moment.ISO_8601).isBefore(moment(), 'days');
  }

  get isTask(): boolean {
    return this.task.task_type === 'task';
  }

  get isProject(): boolean {
    const relatedTo = this.store.selectSnapshot(TaskState.getTaskRelatedTo).relatedToType;
    const taskView = this.store.selectSnapshot(TaskState.getTaskView) as TaskView;
    const isProject =
      relatedTo === TaskRelatedToModule.Project ? true : taskView.applicable_type === RelatedToType.Project;
    return isProject;
  }

  get shouldHideStartDate(): boolean {
    const relatedTo = this.store.selectSnapshot(TaskState.getTaskRelatedTo).relatedToType;
    const taskView = this.store.selectSnapshot(TaskState.getTaskView) as TaskView;
    const isProject =
      relatedTo === TaskRelatedToModule.Project ? true : taskView.applicable_type === RelatedToType.Project;
    return (
      !!this.store.selectSnapshot(AccountState.isFeatureEnabled(FeatureFlag.KEY_DATE_LINKING)) &&
      this.isKeyDate &&
      isProject
    );
  }
}
